import { CarrierBillingAccountType } from "@redotech/cockroachdb-codegen/congodb/carrier-billing-account-type";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { carrierToLabel } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo } from "react";

const CARRIER_OPTIONS = [
  { value: Carriers.UPS, label: carrierToLabel[Carriers.UPS] },
  { value: Carriers.FEDEX, label: carrierToLabel[Carriers.FEDEX] },
];

export const BillingAccountInput = groupInput({
  accountName: input<string>({ validator: nonEmptyValidator }),
  carrier: input<Carriers>({ validator: nonEmptyValidator }),
  accountNumber: input<string>({ validator: nonEmptyValidator }),
  country: input<string>({ validator: nonEmptyValidator }),
  postalCode: input<string>({ validator: nonEmptyValidator }),
});

export type BillingAccountValue = InputSchema.Value<typeof BillingAccountInput>;

export type BillingAccountForm = InputSchema.Input<typeof BillingAccountInput>;

export const billingAccountDefault = (): BillingAccountValue => ({
  accountName: "",
  carrier: Carriers.UPS,
  accountNumber: "",
  country: "",
  postalCode: "",
});

export const CreateBillingAccountForm = memo(function CreateBillingAccountForm({
  form,
  billingType,
}: {
  form: BillingAccountForm;
  billingType: CarrierBillingAccountType;
}) {
  const { t } = useTranslation();
  const isReceiver = billingType === CarrierBillingAccountType.Receiver;
  return (
    <Flex dir="column" gap="lg">
      <RedoTextInput
        label={t("Account name")}
        placeholder={t("Enter an identifier for this account")}
        setValue={form.inputs.accountName.setValue}
        value={form.inputs.accountName.value}
      />

      <RedoSingleSelectDropdownInput
        label={t("Carrier")}
        options={CARRIER_OPTIONS.map((carrier) => ({
          id: carrier.value,
          type: "text",
          text: carrier.label,
          value: carrier.value,
        }))}
        optionSelected={({ item }) => {
          form.inputs.carrier.setValue(item.value as Carriers);
        }}
        placeholder={t("Select a carrier")}
        selectedItem={{
          id: form.inputs.carrier.value,
          type: "text",
          text: carrierToLabel[form.inputs.carrier.value],
          value: form.inputs.carrier.value,
        }}
      />
      {form.inputs.carrier.value === Carriers.UPS && (
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {isReceiver
            ? t(
                "{{carrier}} does not support Bill Receiver for {{service}} rates or for return labels. These rates will not be available for purchase with receiver billing. ",
                { carrier: "UPS®", service: "UPS Ground Saver®" },
              )
            : t(
                "{{carrier}} does not support Third Party Billing for {{service}} rates. These rates will not be available for purchase with Third Party Billing. ",
                { carrier: "UPS®", service: "UPS Ground Saver®" },
              )}
          <a
            href="https://www.ups.com/assets/resources/webcontent/en_US/SurePost_Terms.pdf"
            rel="noreferrer"
            target="_blank"
          >
            <Text as="span" textColor="secondary" textDecoration="underline">
              Terms of Service
            </Text>
          </a>
        </Text>
      )}

      <RedoTextInput
        label={t("Account number")}
        placeholder={t("Enter account number")}
        setValue={form.inputs.accountNumber.setValue}
        value={form.inputs.accountNumber.value}
      />

      <RedoTextInput
        label={t("Country code")}
        maxLength={2}
        placeholder={t("Enter country code")}
        setValue={form.inputs.country.setValue}
        value={form.inputs.country.value}
      />

      <RedoTextInput
        description={
          isReceiver
            ? t(
                "Must match the pickup postal code on the receiver's carrier account",
              )
            : undefined
        }
        label={t("Postal code")}
        placeholder={t("Enter postal code")}
        setValue={form.inputs.postalCode.setValue}
        value={form.inputs.postalCode.value}
      />
    </Flex>
  );
});
